import { useEffect, useState, useRef, type CSSProperties } from 'react';
import { apiFetch } from '@/lib/auth';
import { useUIStore } from '@/state/store';
import { listLocalDocs, deleteLocalDoc, countLocalDocs, markDownloaded, type LocalDoc } from '@/lib/localDocs';
import { dirPickerSupported, pickDir, getAllDirNames, clearDir, saveToDir, saveWithDialog, getHandles, getAccessStates, requestAccessAll, pickInboxDir, getInboxDirName, clearInboxDir, getInboxAccess, getInboxHandle, requestInboxAccess, type TargetKind, type AccessState } from '@/lib/fileTargets';
import { stampForExport } from '@/lib/docMeta';
import { pickRecordingDir, getRecordingDirName, clearRecordingDir, getRecordingAccess, requestRecordingAccess, getRecordingHandle, getAllRecordingLogs, getRecipientKey, setRecipientKey, clearRecipientKey, type RecAccess, type RecordingLogEntry } from '@/lib/recordingDelivery';
import { inspectPublicKey } from '@/lib/recordingCrypto';

const KIND_DIRS: { kind: TargetKind; label: string }[] = [
  { kind: 'scan', label: 'Scans' },
  { kind: 'signed', label: 'Unterschriebene Verträge' },
  { kind: 'nfc', label: 'EC-Karten-Protokolle' },
];

const TOPICS: { key: string; label: string }[] = [
  { key: 'konto', label: 'Konto' },
  { key: 'karte', label: 'Karte' },
  { key: 'online-banking', label: 'Online-Banking' },
  { key: 'ueberweisung', label: 'Überweisung' },
  { key: 'geldanlage', label: 'Geldanlage' },
  { key: 'kredit', label: 'Kredit' },
  { key: 'baufinanzierung', label: 'Baufinanzierung' },
  { key: 'versicherung', label: 'Versicherung' },
  { key: 'beschwerde', label: 'Beschwerde' },
  { key: 'sonstiges', label: 'Sonstiges' },
];

interface PendingCall {
  id: string;
  steleName: string | null;
  location: string | null;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  rating: number | null;
  signatureCount: number;
  wrapDone: boolean;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtDur(sec: number | null): string {
  if (sec == null) return '–';
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')} min`;
}
function kindLabel(k: string): string {
  return k === 'signed' ? 'SIG' : k === 'nfc' ? 'EC' : 'SCAN';
}

export function WrapupView() {
  const [items, setItems] = useState<PendingCall[]>([]);
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});
  const [recLogs, setRecLogs] = useState<Record<string, RecordingLogEntry>>({});
  useEffect(() => { getAllRecordingLogs().then(setRecLogs).catch(() => { /* */ }); }, []);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const openSettings = useUIStore((s) => s.openSettings);

  // pre158: Ordner-/PGP-Konfiguration ist in die Einstellungen (Tab „Ablage & Ordner")
  // umgezogen. Hier wird nur noch gelesen, welche Zielordner gesetzt sind — für die
  // „In Ordner ablegen"-Aktion je Dokument in den Karten.
  const [dirNames, setDirNames] = useState<Record<TargetKind, string | null>>({ scan: null, signed: null, nfc: null });
  useEffect(() => { getAllDirNames().then(setDirNames).catch(() => { /* */ }); }, []);

  useEffect(() => {
    let alive = true;
    apiFetch('/api/calls/pending-wrapup')
      .then((r) => (r.ok ? r.json() : []))
      .then(async (x) => {
        if (!alive || !Array.isArray(x)) return;
        setItems(x);
        const counts: Record<string, number> = {};
        await Promise.all(x.map(async (c: PendingCall) => { counts[c.id] = await countLocalDocs(c.id); }));
        if (alive) setDocCounts(counts);
      })
      .catch(() => { /* */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const openCount = items.filter((i) => !i.wrapDone).length;
  const setDocCount = (id: string, n: number) => setDocCounts((m) => ({ ...m, [id]: n }));
  const markWrapDone = (id: string) => setItems((xs) => xs.map((i) => (i.id === id ? { ...i, wrapDone: true } : i)));

  return (
    <div style={wrap}>
      <div style={head}>
        <div>
          <div style={title}>Nachbearbeitung</div>
          <div style={sub}>Beendete Gespräche der letzten 14 Tage</div>
        </div>
        {openCount > 0 && <div style={countBadge}>{openCount} offen</div>}
      </div>

      <div style={privacyNote}>
        🔒 Die in einem Gespräch erfassten Dokumente (Scans, signierte PDFs, EC-Karten-Protokolle) liegen ausschließlich
        lokal in diesem Browser und werden nicht an den Server übertragen. Speichere sie hier in deinem System ab.
      </div>

      <button
        onClick={() => openSettings('storage')}
        style={{ ...dirBar, width: '100%', textAlign: 'left', cursor: 'pointer' }}
        title="Zielordner & Empfaenger-Schluessel in den Einstellungen verwalten"
      >
        <span style={{ fontSize: 16 }}>\U0001F4C1</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
          Zielordner & PGP-Empfaenger werden jetzt unter <strong style={{ color: 'var(--fg)' }}>Einstellungen → Ablage &amp; Ordner</strong> verwaltet.
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tenant-accent)', flexShrink: 0 }}>Oeffnen →</span>
      </button>

      {loading ? (
        <div style={muted}>Lädt …</div>
      ) : items.length === 0 ? (
        <div style={emptyBox}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Keine Gespräche</div>
          <div style={muted}>In den letzten 14 Tagen gab es keine beendeten Gespräche.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((c) => (
            <WrapupCard key={c.id} call={c} docCount={docCounts[c.id] ?? 0} open={openId === c.id} dirNames={dirNames}
              recInfo={recLogs[c.id]}
              onToggle={() => setOpenId((id) => (id === c.id ? null : c.id))}
              onDocCount={(n) => setDocCount(c.id, n)}
              onWrapDone={() => markWrapDone(c.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function WrapupCard({ call, docCount, open, dirNames, recInfo, onToggle, onDocCount, onWrapDone }: {
  call: PendingCall; docCount: number; open: boolean; dirNames: Record<TargetKind, string | null>; onToggle: () => void;
  recInfo?: RecordingLogEntry;
  onDocCount: (n: number) => void; onWrapDone: () => void;
}) {
  const [docs, setDocs] = useState<LocalDoc[] | null>(null);
  const [resolved, setResolved] = useState<boolean | null>(null);
  const [topics, setTopics] = useState<Set<string>>(new Set());
  const [quality, setQuality] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || docs !== null) return;
    listLocalDocs(call.id).then((d) => setDocs(d)).catch(() => setDocs([]));
  }, [open, docs, call.id]);

  const toggleTopic = (k: string) => setTopics((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  async function delDoc(docId: string) {
    try { await deleteLocalDoc(docId); const next = (docs ?? []).filter((d) => d.docId !== docId); setDocs(next); onDocCount(next.length); } catch { /* */ }
  }
  const markLocal = (docId: string) => {
    void markDownloaded(docId);
    setDocs((xs) => (xs ?? []).map((x) => (x.docId === docId ? { ...x, downloaded: true } : x)));
  };
  const anyDir = !!(dirNames.scan || dirNames.signed || dirNames.nfc);
  async function saveToFolder(d: LocalDoc) {
    const { blob, name } = await stampForExport(d);
    const hasDir = !!dirNames[d.kind];
    if (hasDir) {
      const res = await saveToDir(d.kind, name, blob, { interactive: true });
      if (res.ok) { markLocal(d.docId); return; }
      setErr(res.reason === 'denied'
        ? 'Kein Schreibzugriff auf den Zielordner — „Speichern unter …" geöffnet. Ordner ggf. neu wählen.'
        : 'Ablegen im Ordner fehlgeschlagen — „Speichern unter …" geöffnet.');
    }
    const out = await saveWithDialog(name, blob, d.kind);
    if (out !== 'cancelled') markLocal(d.docId);
  }
  async function saveAs(d: LocalDoc) {
    setErr('');
    const { blob, name } = await stampForExport(d);
    const out = await saveWithDialog(name, blob, d.kind);
    if (out !== 'cancelled') markLocal(d.docId);
  }
  function downloadAll() {
    (docs ?? []).forEach((d, i) => setTimeout(() => { void saveToFolder(d); }, i * 200));
  }
  async function complete() {
    setBusy(true); setErr('');
    try {
      const body: any = {};
      if (resolved !== null) body.resolved = resolved;
      if (topics.size > 0) body.topics = Array.from(topics);
      if (quality > 0) body.quality = quality;
      const r = await apiFetch(`/api/calls/${call.id}/agent-feedback`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) onWrapDone();
      else setErr('Abschließen fehlgeschlagen.');
    } catch { setErr('Abschließen fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ ...card, ...(call.wrapDone ? {} : { borderColor: 'var(--tenant-accent-soft)' }) }}>
      <div style={cardTop} onClick={onToggle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            {call.steleName || 'Stele'}{call.location ? <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {call.location}</span> : null}
            {call.wrapDone ? <span style={tagDone}>bearbeitet</span> : <span style={tagOpen}>offen</span>}
            {recInfo && (
              <span style={{
                fontSize: 10, fontWeight: 800, letterSpacing: '.06em', padding: '2px 7px', borderRadius: 999,
                background: recInfo.ok ? 'var(--tenant-accent, #E2001A)' : '#9A9A9A', color: '#fff',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }} title={recInfo.ok ? `Aufzeichnung abgelegt: ${recInfo.fileName}` : 'Aufzeichnung — Ablage fehlgeschlagen'}>
                ● REC
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>{fmtDate(call.endedAt)}</span>
            <span>{fmtDur(call.durationSec)}</span>
            {call.rating ? <span>★ {call.rating}/5</span> : null}
            <span>📎 {docCount} Dok.</span>
            {recInfo && (
              <span style={{ color: recInfo.ok ? 'var(--fg)' : '#DC2626' }} title={recInfo.fileName}>
                🔴 {recInfo.ok ? `Aufzeichnung abgelegt: ${recInfo.fileName}` : 'Aufzeichnung: Ablage fehlgeschlagen'}
              </span>
            )}
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▾</div>
      </div>

      {open && (
        <div style={cardBody}>
          {err && <div style={{ color: '#d8785a', fontSize: 12, marginBottom: 8 }}>{err}</div>}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 8px' }}>
            <span style={blockLabel}>Erfasste Dokumente</span>
            {docs && docs.length > 1 && <button onClick={downloadAll} style={miniBtn}>{anyDir ? 'Alle ablegen' : 'Alle herunterladen'}</button>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {docs === null ? <span style={muted}>Lädt …</span>
              : docs.length === 0 ? <span style={muted}>In diesem Gespräch wurden keine Dokumente erfasst.</span>
              : docs.map((d) => (
                <div key={d.docId} style={docRow}>
                  <span style={kindTag(d.kind)}>{kindLabel(d.kind)}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5 }}>{d.name}</span>
                  {d.downloaded && <span style={{ fontSize: 10.5, color: 'var(--status-available)', fontWeight: 700 }} title="Bereits abgelegt">✓ abgelegt</span>}
                  <button onClick={() => saveToFolder(d)} style={{ ...linkBtn, color: 'var(--info)' }} title={dirNames[d.kind] ? 'In den Typ-Ordner ablegen' : 'Speichern'}>{dirNames[d.kind] ? 'Ablegen' : 'Herunterladen'}</button>
                  <button onClick={() => saveAs(d)} style={{ ...linkBtn, color: 'var(--fg-soft)' }} title="Ort frei wählen">Speichern unter …</button>
                  <button onClick={() => delDoc(d.docId)} style={{ ...linkBtn, color: '#DC2626' }} title="Aus lokalem Zwischenspeicher entfernen">Entfernen</button>
                </div>
              ))}
          </div>

          {call.wrapDone ? (
            <div style={{ ...muted, padding: '4px 0' }}>Bewertung wurde bereits erfasst.</div>
          ) : (
            <>
              <div style={blockLabel}>Anliegen abgeschlossen?</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                <button onClick={() => setResolved(true)} style={{ ...pill, ...(resolved === true ? pillOn : {}) }}>Ja</button>
                <button onClick={() => setResolved(false)} style={{ ...pill, ...(resolved === false ? pillOn : {}) }}>Nein</button>
              </div>

              <div style={blockLabel}>Themen</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {TOPICS.map((t) => (
                  <button key={t.key} onClick={() => toggleTopic(t.key)} style={{ ...pill, ...(topics.has(t.key) ? pillOn : {}) }}>{t.label}</button>
                ))}
              </div>

              <div style={blockLabel}>Gesprächsqualität</div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setQuality(n)} style={{ ...pill, width: 34, ...(quality >= n ? pillOn : {}) }}>★</button>
                ))}
              </div>

              <button onClick={complete} disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>Nachbearbeitung abschließen</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const wrap: CSSProperties = { flex: 1, overflowY: 'auto', padding: '32px 40px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720, width: '100%', margin: '0 auto' };
const head: CSSProperties = { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' };
const title: CSSProperties = { fontSize: 28, fontWeight: 300, letterSpacing: '-.02em' };
const sub: CSSProperties = { fontSize: 12, color: 'var(--muted)', marginTop: 4 };
const countBadge: CSSProperties = { background: 'var(--tenant-accent)', color: '#fff', fontWeight: 600, fontSize: 12, borderRadius: 999, padding: '4px 12px' };
const privacyNote: CSSProperties = { fontSize: 11.5, color: 'var(--muted)', background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '8px 12px', lineHeight: 1.5 };
const dirBar: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-elev-1)', border: '1px solid var(--hairline)', borderRadius: 8 };
const confirmBar: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--tenant-accent-soft)', border: '1px solid var(--tenant-accent-soft)', borderRadius: 8 };
const muted: CSSProperties = { fontSize: 13, color: 'var(--muted)' };
const emptyBox: CSSProperties = { textAlign: 'center', padding: '48px 0', color: 'var(--fg-soft)' };
const card: CSSProperties = { background: 'var(--bg-elev-1)', border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden' };
const cardTop: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer' };
const cardBody: CSSProperties = { padding: '4px 16px 16px', borderTop: '1px solid var(--hairline-soft)' };
const blockLabel: CSSProperties = { fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600 };
const docRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', borderRadius: 6 };
const pill: CSSProperties = { border: '1px solid var(--hairline)', background: 'var(--bg-elev-2)', color: 'var(--fg-soft)', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
const pillOn: CSSProperties = { background: 'var(--tenant-accent)', borderColor: 'var(--tenant-accent)', color: '#fff' };
const miniBtn: CSSProperties = { border: '1px solid var(--hairline)', background: 'var(--bg-elev-2)', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: 'inherit' };
const linkBtn: CSSProperties = { border: 'none', background: 'transparent', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none' };
const primaryBtn: CSSProperties = { border: 'none', background: 'var(--tenant-accent)', color: '#fff', borderRadius: 8, padding: '11px 18px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: '100%' };
const tagDone: CSSProperties = { fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--status-available)', background: '#22c55e1a', border: '1px solid #22c55e40', borderRadius: 999, padding: '1px 7px' };
const tagOpen: CSSProperties = { fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--tenant-accent)', background: 'var(--tenant-accent-soft)', border: '1px solid var(--tenant-accent-soft)', borderRadius: 999, padding: '1px 7px' };
function kindTag(kind: string): CSSProperties {
  const base: CSSProperties = { fontSize: 8.5, fontWeight: 700, letterSpacing: '.06em', padding: '2px 5px', borderRadius: 4, flexShrink: 0, width: 34, textAlign: 'center' };
  if (kind === 'signed') return { ...base, color: '#22c55e', background: '#22c55e26' };
  if (kind === 'nfc') return { ...base, color: '#3b82f6', background: '#3b82f626' };
  return { ...base, color: 'var(--muted)', background: 'var(--bg-elev-3)' };
}
